import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { toast } from '@/components/ui/use-toast'
import { putFetcher } from 'api/swr'
import { applyMixedSpineOutfitInAvatarEditor } from 'Editor/lib/applyMixedSpineOutfit'
import { applyStaticOutfitToEditor } from 'Editor/lib/applyStaticOutfit'
import { resolveAvatarOutfit } from 'commons/avatarOutfit'
import { ChevronDownIcon } from '@radix-ui/react-icons'
import { Loader2 } from 'lucide-react'
import { useState, type MouseEvent } from 'react'

type DefaultSetGotoEditorMenuProps = {
  avatar: {
    id?: string
    drawData?: string
    spineDrawData?: string
    engineWith?: string
  }
  tid: string
  onPinned?: () => void | Promise<void>
}

export function DefaultSetGotoEditorMenu({
  avatar,
  tid,
  onPinned,
}: DefaultSetGotoEditorMenuProps) {
  const [pending, setPending] = useState(false)
  const [pinPending, setPinPending] = useState(false)

  const handleGoToEditor = async (e: MouseEvent<HTMLDivElement>) => {
    e.preventDefault()
    if (pending) return
    setPending(true)
    try {
      const outfit = await resolveAvatarOutfit(avatar)
      const templateId = outfit.tid || tid
      await applyStaticOutfitToEditor(templateId, outfit)

      const spineDrawData = outfit.spineDrawData
      if (spineDrawData?.trim()) {
        const result = await applyMixedSpineOutfitInAvatarEditor({
          spineDrawData,
          tid: templateId,
        })
        if (result.ok === false) {
          toast({
            variant: 'destructive',
            title: 'Spine 组件未完全还原',
            description: result.message,
          })
          return
        }
        toast({
          title: '已还原到编辑器',
          description: '普通图层与关联 Spine 组件已应用',
          className: 'bg-green-500 text-white',
        })
        return
      }

      toast({
        title: '已还原到编辑器',
        description: '图层与染色已应用',
        className: 'bg-green-500 text-white',
      })
    } catch {
      toast({
        variant: 'destructive',
        title: 'Failed',
        description: 'Invalid drawData',
      })
    } finally {
      setPending(false)
    }
  }

  const handlePin = async (e: MouseEvent<HTMLDivElement>) => {
    e.preventDefault()
    if (pinPending || !avatar?.id) return
    setPinPending(true)
    try {
      await putFetcher(`/avatars/${avatar.id}/pin`, { arg: {} })
      toast({
        title: 'Pinned',
        description: avatar.id,
        className: 'bg-green-500 text-white',
      })
      await onPinned?.()
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Pin failed',
        description: err instanceof Error ? err.message : 'Unknown error',
      })
    } finally {
      setPinPending(false)
    }
  }

  if (!avatar?.drawData && !avatar?.id) {
    return null
  }

  const busy = pending || pinPending

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-7 w-7 shrink-0 bg-background shadow-sm"
          title="Actions"
          disabled={busy}
          onClick={(e: MouseEvent<HTMLButtonElement>) => e.stopPropagation()}
        >
          {busy ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <ChevronDownIcon className="h-3.5 w-3.5" />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {avatar?.id ? (
          <DropdownMenuItem
            disabled={pinPending}
            onClick={(e) => void handlePin(e)}
          >
            {pinPending ? 'Pinning…' : 'Pin to top'}
          </DropdownMenuItem>
        ) : null}
        {avatar?.id ? (
          <DropdownMenuItem
            disabled={pending}
            onClick={(e) => void handleGoToEditor(e)}
          >
            {pending ? 'Loading…' : 'Go to editor'}
          </DropdownMenuItem>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
